import type { GetStaticProps, NextPage } from 'next'
import Image from 'next/image'
import { IPlace } from 'shared/zod-models/place'
import styles from '../styles/Home.module.css'
import { getPlaces } from '../data/places'
import React from 'react'
import { useSnackbar } from 'notistack'
import { MetaData, metaDataSchema } from '../validations/meta-data'
import { getWithExpiry, setWithExpiry } from '../util/localStorage'
import Common from './common'

const MAX_NUM_DAILY_ROLLS = 3 as const
const LOCAL_STORAGE_KEY = 'where-to-eat-meta-data' as const

interface HomeProps {
  places: IPlace[]
}

export const getStaticProps: GetStaticProps<HomeProps> = async context => {
  // Store places in the frontend at build time
  const places = await getPlaces()
  return {
    props: {
      // https://github.com/vercel/next.js/discussions/11209#discussioncomment-35915
      places: JSON.parse(JSON.stringify(places)),
    },
    revalidate: 60 * 60 * 24, // 1 day ISR
  }
}

interface CardProps {
  place: IPlace
}
const Card: React.FC<CardProps> = ({ place }) => {
  const emojiPrice = '💲'.repeat(place?.price || 0)
  return (
    <div className={styles.card}>
      <h1>{place.name}</h1>
      <p>{emojiPrice}</p>
      <p>
        <a href={getGoogleMapsLink(place.name, place.address)} target="_blank" rel="noreferrer">
          <Image alt="google map pin" src="/google-maps-pin.png" width={16} height={16} />
          {place.address}
        </a>
      </p>
      {place.image_url && (
        <Image
          unoptimized={true}
          height={400}
          width={400}
          alt="image related to suggested place"
          src={place.image_url}
        ></Image>
      )}
      <div className={styles.subtle}>
        {place.url && (
          <p>
            Information provided by{' '}
            <a href={place.url} target="_blank" rel="noreferrer">
              <Image alt="yelp logo" src="/yelp_logo.png" height={16} width={42} />
            </a>
          </p>
        )}
      </div>
    </div>
  )
}

const pickRandomPlace = (places: IPlace[]): IPlace => {
  const randomIndex = Math.floor(Math.random() * places.length)
  return places[randomIndex]
}

const getGoogleMapsLink = (name: string, address: string): string => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    name + ', ' + address
  )}`
}

const getMidnightTonightForClient = (): Date => {
  const now = new Date()
  const day = now.getDate()
  const month = now.getMonth()
  const year = now.getFullYear()

  // This should be the midnight of the current day of the client's timezone
  const midnightTonight = new Date(year, month, day, 23, 59, 59)
  return midnightTonight
}

const Home: NextPage<HomeProps> = ({ places }) => {
  const [selectedPlace, setSelectedPlace] = React.useState<IPlace | null>(null)

  const [metaData, setMetaData] = React.useState<MetaData>(
    (typeof window !== 'undefined' && getWithExpiry(window, LOCAL_STORAGE_KEY, metaDataSchema)) || {
      numberOfRolls: 0,
      lastPlace: {
        name: '',
        address: '',
        price: 0,
        url: '',
      },
    }
  )
  const incrementMetaData = (lastPlace: IPlace) => {
    setMetaData(metaData => {
      const newMetaData = { ...metaData, lastPlace, numberOfRolls: metaData.numberOfRolls + 1 }
      const expiry = getMidnightTonightForClient()
      setWithExpiry(window, LOCAL_STORAGE_KEY, newMetaData, metaDataSchema, expiry)
      return newMetaData
    })
  }

  const { enqueueSnackbar } = useSnackbar()

  const handleClick = () => {
    if (metaData.numberOfRolls < MAX_NUM_DAILY_ROLLS) {
      const randomPlace = pickRandomPlace(places)
      setSelectedPlace(randomPlace)
      if (metaData.numberOfRolls === MAX_NUM_DAILY_ROLLS - 1) {
        enqueueSnackbar("You've used up all you rerolls. Bon Appétit 🍽", {
          variant: 'info',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        })
      } else if (metaData.numberOfRolls > 0) {
        enqueueSnackbar(`${MAX_NUM_DAILY_ROLLS - (metaData.numberOfRolls + 1)} reroll left`, {
          variant: 'info',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        })
      }
      incrementMetaData(randomPlace)
    } else {
      setSelectedPlace(metaData.lastPlace)
      enqueueSnackbar(`You've already rerolled ${MAX_NUM_DAILY_ROLLS} times today.`, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      })
    }
  }

  return (
    <Common>
      <p className={styles.description}>
        <a href="https://en.wikipedia.org/wiki/Overchoice">Choice overload</a>? No options, no pros
        and cons lists, no risk matrices, no SWOT analyses, no ROI calcs needed.
      </p>
      <p className={styles.description}>
        No stress, just hit <code className={styles.code}>random</code>.
      </p>

      <div className={styles.app}>
        <button onClick={handleClick}>Random</button>
      </div>
      {selectedPlace && <Card place={selectedPlace} />}
    </Common>
  )
}

export default Home
